import React, { useEffect, useRef } from 'react';
import { TURNSTILE_ACTION } from '../../constants';
import { loadTurnstile, TurnstileGlobal } from '../../utils/turnstileLoader';

interface TurnstileWidgetProps {
  siteKey: string;
  onToken: (token: string) => void;
  onExpire: () => void;
  // The script failed to load, or the widget reported an error.
  onError: () => void;
}

// The Cloudflare Turnstile challenge on the signup form. Renders the widget
// once per mount; the parent resets it by remounting with a new key, since a
// token is single-use and is spent by every signUp call.
const TurnstileWidget: React.FC<TurnstileWidgetProps> = ({ siteKey, onToken, onExpire, onError }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  // Held in refs so the parent's inline arrows never re-run the effect,
  // which would tear the widget down and spend the visitor's challenge.
  const onTokenRef = useRef(onToken);
  const onExpireRef = useRef(onExpire);
  const onErrorRef = useRef(onError);
  onTokenRef.current = onToken;
  onExpireRef.current = onExpire;
  onErrorRef.current = onError;

  useEffect(() => {
    let cancelled = false;
    let widgetId: string | undefined;
    let api: TurnstileGlobal | undefined;

    loadTurnstile()
      .then((turnstile) => {
        if (cancelled || !containerRef.current) return;
        api = turnstile;
        widgetId = turnstile.render(containerRef.current, {
          sitekey: siteKey,
          action: TURNSTILE_ACTION,
          callback: (token) => onTokenRef.current(token),
          'error-callback': () => onErrorRef.current(),
          'expired-callback': () => onExpireRef.current(),
        });
      })
      .catch(() => {
        if (!cancelled) onErrorRef.current();
      });

    return () => {
      cancelled = true;
      if (api && widgetId) api.remove(widgetId);
    };
  }, [siteKey]);

  return <div ref={containerRef} className="signup-turnstile" />;
};

export default TurnstileWidget;
